import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { ApiError, post } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S } from "@/lib/strings";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { Operator } from "@/types/fleet";

export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();

  const next = params.get("next") ?? "/overview";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError(S.loginNeedBoth);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await post<{ operator: Operator }>("/auth/login", {
        username: username.trim(),
        password,
      });
      // The session cookie is set by the response; drop any cached identity from
      // a previous operator before the shell re-gates on the new one.
      qc.removeQueries({ queryKey: qk.me });
      navigate(next, { replace: true });
    } catch (err) {
      // 401 here is a wrong password, not an expired session — say so, rather
      // than bouncing back to this same page with no explanation.
      setError(
        err instanceof ApiError && err.status === 401 ? S.loginFailed : (err as Error).message,
      );
      setPassword("");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-canvas)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="grid size-8 place-items-center rounded-lg bg-gradient-to-br from-[#5e6ad2] to-[#7170ff] text-[11px] font-semibold text-white">
            IC
          </div>
          <span className="text-base font-semibold tracking-[-0.24px]">{S.appName}</span>
        </div>

        <h1 className="text-lg font-semibold tracking-[-0.24px]">{S.loginTitle}</h1>
        <p className="mt-1 text-xs text-[var(--color-fg-subtle)]">{S.loginSubtitle}</p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label htmlFor="username" className="mb-1 block text-[11px] font-medium text-[var(--color-fg-subtle)]">
              {S.loginUsername}
            </label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-[11px] font-medium text-[var(--color-fg-subtle)]">
              {S.loginPassword}
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <p role="alert" className="text-xs text-[#fca5a5]">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" disabled={pending} className="w-full justify-center">
            {pending && <Loader2 size={13} className="animate-spin" />}
            {pending ? S.loginPending : S.loginSubmit}
          </Button>
        </form>
      </div>
    </div>
  );
}
